// Library Methods
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCallback } from "react";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import DownloadButtons from "./DownloadButtons/DownloadButtons";
import ExamTransferModal from "../ExamTransferModal/ExamTransferModal";
import { useTheme } from "@mui/material/styles";
import { UserEditIcon } from "src/assets/icons";

// Styles
import { ModalCustomConfirmBlack } from "src/styles/mui/muiStylesHelper";

// Types
import { PatientType } from "src/services/patients/types";
import { ReportFileType } from "src/constants/enums";
import { FileInfoType } from "src/types/sharedTypes";

// Hooks
import { useTransferExam } from "src/services/exams/hooks/useTransferExam";

// Contexts
import { useExamTransferContext } from "src/contexts/exams/ExamTransferContext";
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type ReportButtonsType = {
  patient: PatientType | undefined;
  examId: string;
  isLoading: boolean;
  showTransferButton: boolean;
  showDownloadButtons: boolean;
  reportGenerationProps: {
    downloadFile: (fileType: ReportFileType) => Promise<void>;
    getReportInfoData: () => Promise<void>;
    showDICOMButton: boolean;
    showPNGButton: boolean;
    fileInfo: {
      [key in ReportFileType]: FileInfoType;
    };
    reportInfoError: boolean;
  };
};

const ReportButtons = ({
  patient,
  examId,
  isLoading,
  showTransferButton,
  showDownloadButtons,
  reportGenerationProps,
}: ReportButtonsType) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clinicInfo } = useClinicContext();

  const { mutate: transferMutate } = useTransferExam(clinicInfo?.id);

  const { openTransferModal, actions: transferActions } =
    useExamTransferContext();
  const { handleTransferClick, handleTransferClose } = transferActions;

  const confirmTransferExam = useCallback(
    (props: {
      patientId: string;
      examId: string | undefined;
      newPatientId: string;
    }) => {
      transferMutate(
        { ...props },
        {
          onSuccess: () => {
            // navigate to new exam page
            const { newPatientId } = props;
            navigate(`/patients/${newPatientId}/exam/${examId}`);
            window.location.href = `/patients/${newPatientId}/exam/${examId}`;
            handleTransferClose();
          },
        }
      );
    },
    [transferMutate, handleTransferClose, navigate, examId]
  );

  return (
    <Box
      sx={{
        display: "flex",
        gap: "16px",
        flexWrap: "wrap",
        flexDirection: { xs: "column", sm: "row" },
      }}
    >
      {isLoading ? (
        <Skeleton
          hidden={!showTransferButton}
          variant="rounded"
          sx={{
            height: 40,
            width: { xs: "100%", sm: "200px" },
            borderRadius: "4px",
          }}
        />
      ) : (
        <Button
          hidden={!showTransferButton}
          startIcon={<UserEditIcon />}
          onClick={() => handleTransferClick(examId)}
          sx={{
            ...ModalCustomConfirmBlack(theme),
            minWidth: "fit-content",
            width: { xs: "100%", sm: "200px" },
          }}
        >
          <Typography sx={{ textTransform: "none" }}>
            {t("Reassign Exam")}
          </Typography>
        </Button>
      )}

      <DownloadButtons
        isLoading={isLoading}
        showDownloadButtons={showDownloadButtons}
        {...reportGenerationProps}
      />

      {patient && showTransferButton && (
        <ExamTransferModal
          open={openTransferModal}
          currentPatient={patient}
          onClose={handleTransferClose}
          onConfirm={confirmTransferExam}
        />
      )}
    </Box>
  );
};

export default ReportButtons;
